import { createReducer, on } from '@ngrx/store';
import { classifySurvey } from '@ps/survey-utils';

import {
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_CORE_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationCoreState } from '../survey-creation-state.model';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from './survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
  SurveyCreationChildrenApiActions,
  SurveyCreationMcApiActions,
  SurveyCreationPreDraftActions,
} from '../audience/survey-creation-audience.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from '../launch/survey-creation-launch.actions';
import { buildLoadedRegistryChild } from '../audience/domain/build-audience-form-state-from-survey';
import {
  buildPreDraftCoreChildren,
  describesActiveCoreChild,
  initializeCoreState,
  isLoadedCoreSibling,
  patchCoreChildren,
  resolveLoadedActiveChildKey,
  resolveReTargetedActiveChildKey,
  resolveLoadedParticipant,
} from './utils/core-children.helper';
import { omitChild } from '../audience/utils/audience-children.helper';
import {
  CHILD_SCOPE,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';

export const surveyCreationCoreReducer = createReducer(
  INITIAL_SURVEY_CREATION_CORE_STATE,

  // Cross-listens to both stage-specific init actions — whichever page
  // opened, core has to target the same survey. See
  // SurveyCreationAudienceActions.init's doc comment.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, { surveyId }): SurveyCreationCoreState => {
      const alreadyHydrated = !!surveyId && state.rootSurveyId === surveyId;

      if (alreadyHydrated) {
        return state;
      }

      // A locale switch re-`init`s with a sibling's id. The family is already
      // in the registry, so keep it and only re-target — resetting here would
      // force a refetch of every sibling.
      if (!!surveyId && isLoadedCoreSibling(state.children, surveyId)) {
        return {
          ...state,
          rootSurveyId: surveyId,
          activeChildKey: resolveReTargetedActiveChildKey(state, surveyId),
        };
      }

      return initializeCoreState(surveyId);
    },
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, { surveys }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: {
            ...state.children,
            ...Object.fromEntries(
              surveys
                .filter(({ id }) => !state.children[id])
                .map((survey) => [survey.id, buildLoadedRegistryChild(survey)]),
            ),
          },
        },
        CHILD_SCOPE.keys(surveys.map(({ id }) => id)),
        { error: null },
      ),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          children: {
            ...state.children,
            [survey.id]: buildLoadedRegistryChild(survey),
          },
        },
        CHILD_SCOPE.keys([survey.id]),
        { error: null },
      ),
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationCoreState =>
      patchCoreChildren(
        { ...state, children: omitChild(state.children, surveyId) },
        CHILD_SCOPE.ALL,
        { error: null },
      ),
  ),

  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), {
        survey,
        error: null,
      }),
  ),

  on(
    SurveyCreationChildrenApiActions.updateChildSurveyCpiFailure,
    (state, { surveyId, error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.keys([surveyId]), { error }),
  ),

  on(
    SurveyCreationMcApiActions.addSurveyChildFailure,
    SurveyCreationMcApiActions.deleteSurveyChildFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreActions.setActiveChildKey,
    (state, { activeChildKey }): SurveyCreationCoreState => ({
      ...state,
      activeChildKey,
    }),
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(
        {
          ...state,
          rootSurveyId: null,
          activeChildKey: null,
          kind: null,
          children: delegateRecordToDefaultChild(
            state.children,
            state.activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY,
          ),
        },
        CHILD_SCOPE.keys([DEFAULT_SURVEY_CHILD_KEY]),
        { survey: null, error: null, participant: null },
      ),
  ),

  // Pre-draft only: before a surveyId exists, each locale gets its own
  // registry entry, mirroring audience's own per-locale children.
  on(
    SurveyCreationPreDraftActions.buildPreDraftChildren,
    (state, { localeData }): SurveyCreationCoreState => {
      if (state.rootSurveyId) {
        return state;
      }

      return {
        ...state,
        children: buildPreDraftCoreChildren(state.children, localeData),
      };
    },
  ),

  on(
    SurveyCreationPreDraftActions.selectAllPreDraftChildren,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { participant: true }),
  ),

  on(
    SurveyCreationPreDraftActions.clearAllPreDraftChildren,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { participant: false }),
  ),

  // `ids` is the complete checked set from the pre-draft table, not an
  // incremental target list — every child needs its `participant` flag
  // evaluated against it, so this stays CHILD_SCOPE.ALL rather than
  // CHILD_SCOPE.keys(ids) (which would leave unchecked children untouched
  // instead of flipping them back to false).
  on(
    SurveyCreationPreDraftActions.selectPreDraftChildren,
    (state, { ids }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, (_, key) => ({
        participant: ids.includes(key),
      })),
  ),

  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, { survey }): SurveyCreationCoreState => ({
      ...patchCoreChildren(state, CHILD_SCOPE.keys([survey.id]), {
        survey,
        error: null,
        participant: resolveLoadedParticipant(survey),
      }),
      activeChildKey: resolveLoadedActiveChildKey(state.activeChildKey, survey),
      kind: classifySurvey(survey),
    }),
  ),

  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, { survey }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, {
        survey,
        error: null,
        participant: resolveLoadedParticipant(survey),
      }),
  ),

  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveySuccess,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
    (state, { survey }): SurveyCreationCoreState => ({
      ...state,
      rootSurveyId: survey.id,
      activeChildKey: survey.id,
      kind: classifySurvey(survey),
      children: {
        [survey.id]: {
          survey,
          error: null,
          participant: true,
        },
      },
    }),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveyFailure,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    (state, { survey }): SurveyCreationCoreState =>
      describesActiveCoreChild(state, survey)
        ? {
            ...patchCoreChildren(state, CHILD_SCOPE.ACTIVE, {
              survey,
              error: null,
            }),
            kind: classifySurvey(survey),
          }
        : patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error: null }),
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveyFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  // Patch returns void — no fresh survey comes back — so this merges the
  // TEST button's own patched fields into the registry's own survey directly,
  // instead of tracking a second copy of them elsewhere.
  on(
    SurveyCreationLaunchApiActions.patchTestUrlsSuccess,
    (state, { liveUrl, testUrl, testEntryLink }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, (child) =>
        child.survey
          ? {
              survey: {
                ...child.survey,
                liveUrl,
                testUrl,
                testPsSurveyEntryLink: testEntryLink,
              },
              error: null,
            }
          : {},
      ),
  ),

  on(
    SurveyCreationCoreApiActions.cloneSurveyFailure,
    (state, { error }): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ACTIVE, { error }),
  ),

  // Clearing a stale registry error, not writing per-child data.
  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsSuccess,
    SurveyCreationAudienceApiActions.linkSurveyTagsSuccess,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { error: null }),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state): SurveyCreationCoreState =>
      patchCoreChildren(state, CHILD_SCOPE.ALL, { error: null }),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationCoreState => INITIAL_SURVEY_CREATION_CORE_STATE,
  ),
);
